import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerFansburyMigrationPreviewRoute,
  leagueCustomerFansburyMigrationRemigrateRoute,
  leagueCustomerFansburyMigrationStartRoute,
  leagueCustomerFansburyMigrationStatusRoute,
} from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import GestionTable from "components/pages/Gestion/GestionTable";

const STEP_ORDER = [
  "wipe",
  "users",
  "organizer_roles",
  "branding",
  "sedes",
  "stadiums",
  "referees",
  "teams",
  "team_media",
  "roster_members",
  "follows",
  "tournaments",
  "lineups",
  "lineup_registrations",
  "matches",
  "incidences",
  "sanctions",
  "player_media",
];

const sentenceCase = (value) => {
  if (!value) return "—";
  const spaced = String(value).replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatMigrationStep = (intl, step) =>
  intl.formatMessage({
    id: `gestion.fansbury.step.${step}`,
    defaultMessage: sentenceCase(step),
  });

const formatMigrationStatus = (intl, status) =>
  intl.formatMessage({
    id: `gestion.fansbury.status.${status}`,
    defaultMessage: sentenceCase(status),
  });

const STATUS_CHIP_COLOR = {
  done: "success",
  running: "info",
  failed: "error",
  queued: "default",
  pending: "default",
  skipped: "default",
};

const GestionFansburyMigration = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const { customerId, customerName } = useGestionCustomer();
  const isRevaAdmin = Boolean(user?.is_reva_admin);

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [organizerId, setOrganizerId] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [remigrateOpen, setRemigrateOpen] = useState(false);
  const [remigrateAck, setRemigrateAck] = useState(false);
  const [remigrateConfirm, setRemigrateConfirm] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [chargeHistory, setChargeHistory] = useState(false);
  const [financeImpact, setFinanceImpact] = useState(null);
  const [financeAck, setFinanceAck] = useState(false);
  const hasFinanceImpact = Boolean(financeImpact && Object.values(financeImpact).some((value) => Number(value) > 0));

  const run = status?.run;
  const runStatus = run?.status;
  const isActive = runStatus === "queued" || runStatus === "running";
  const failedRunChargeHistory = runStatus === "failed" ? Boolean(run?.options?.charge_history) : null;

  // A retry must keep the option the failed run was started with.
  useEffect(() => {
    if (failedRunChargeHistory !== null) setChargeHistory(failedRunChargeHistory);
  }, [failedRunChargeHistory]);
  const uuidLocked = Boolean(status?.fansbury_organizer_id) && !isRevaAdmin;

  const loadStatus = useCallback(() => {
    if (!customerId) return Promise.resolve();
    return performRequest("get", leagueCustomerFansburyMigrationStatusRoute(customerId), {}, true, user).then((res) => {
      setStatus(res.data);
      setOrganizerId((current) => current || res.data?.fansbury_organizer_id || "");
      return res.data;
    });
  }, [customerId, user]);

  useEffect(() => {
    if (!customerId) return undefined;
    setStatusLoading(true);
    loadStatus()
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.fansbury.error.status" }), {
          variant: "error",
        });
      })
      .finally(() => setStatusLoading(false));
    return undefined;
  }, [customerId, enqueueSnackbar, intl, loadStatus]);

  useEffect(() => {
    if (!customerId || !isActive) return undefined;
    const timer = setInterval(() => {
      loadStatus().catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
  }, [customerId, isActive, loadStatus]);

  const handlePreview = () => {
    if (!organizerId) return;
    setBusy(true);
    setBusyAction("preview");
    performRequest(
      "post",
      leagueCustomerFansburyMigrationPreviewRoute(customerId),
      { fansbury_organizer_id: organizerId.trim() },
      true,
      user,
      null,
      {},
      false,
      "json",
      90000,
    )
      .then((res) => setPreview(res.data))
      .catch((error) => {
        setPreview(null);
        const timedOut = error?.code === "ECONNABORTED";
        enqueueSnackbar(
          timedOut
            ? intl.formatMessage({ id: "gestion.fansbury.error.previewTimeout" })
            : error?.response?.data?.message || intl.formatMessage({ id: "gestion.fansbury.error.preview" }),
          { variant: "error" },
        );
      })
      .finally(() => {
        setBusy(false);
        setBusyAction(null);
      });
  };

  const handleStart = () => {
    if (!organizerId) return;
    setBusy(true);
    setBusyAction("start");
    performRequest(
      "post",
      leagueCustomerFansburyMigrationStartRoute(customerId),
      { fansbury_organizer_id: organizerId.trim(), charge_history: chargeHistory },
      true,
      user,
    )
      .then((res) => {
        setStatus((prev) => ({ ...prev, run: res.data.run, can_start: false }));
        enqueueSnackbar(intl.formatMessage({ id: "gestion.fansbury.started" }), { variant: "success" });
        return loadStatus();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.fansbury.error.start" }), {
          variant: "error",
        });
      })
      .finally(() => {
        setBusy(false);
        setBusyAction(null);
      });
  };

  const resetRemigrate = () => {
    setRemigrateOpen(false);
    setRemigrateAck(false);
    setRemigrateConfirm("");
    setChallengeId(null);
    setChargeHistory(false);
    setFinanceImpact(null);
    setFinanceAck(false);
  };

  const canSubmitRemigrate =
    remigrateAck &&
    remigrateConfirm.trim() === customerName.trim() &&
    (!challengeId || !hasFinanceImpact || financeAck);

  const handleRemigrate = () => {
    if (!canSubmitRemigrate) return;
    setBusy(true);
    setBusyAction("remigrate");
    const payload = {
      confirm: remigrateConfirm.trim(),
      fansbury_organizer_id: organizerId.trim() || status?.fansbury_organizer_id,
      charge_history: chargeHistory,
    };
    if (challengeId) {
      payload.challenge_id = challengeId;
      payload.acknowledge_finance_loss = Boolean(financeAck);
    }
    performRequest("post", leagueCustomerFansburyMigrationRemigrateRoute(customerId), payload, true, user)
      .then((res) => {
        if (res.data?.challenge_id && !res.data?.run) {
          setChallengeId(res.data.challenge_id);
          setFinanceImpact(res.data.finance_impact ?? null);
          return;
        }
        resetRemigrate();
        enqueueSnackbar(intl.formatMessage({ id: "gestion.fansbury.remigrate.started" }), { variant: "success" });
        return loadStatus();
      })
      .catch((error) => {
        // Finance data appeared after the challenge: restart from step 1 to show fresh impact counts.
        if (error?.response?.status === 422 && error?.response?.data?.errors?.acknowledge_finance_loss) {
          setChallengeId(null);
          setFinanceImpact(null);
          setFinanceAck(false);
        }
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.fansbury.error.remigrate" }), {
          variant: "error",
        });
      })
      .finally(() => {
        setBusy(false);
        setBusyAction(null);
      });
  };

  const counts = preview?.counts || run?.preview?.counts || run?.summary?.counts;
  const displayName = preview?.name || run?.preview?.name;
  const logoUrl = preview?.logo_url || run?.preview?.logo_url;
  const stepEntries = STEP_ORDER.map((step) => [step, run?.steps?.[step]]).filter(([, meta]) => meta);

  const renderChargeHistoryOption = () => (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={chargeHistory}
            onChange={(event) => setChargeHistory(event.target.checked)}
            disabled={busy || Boolean(challengeId)}
            inputProps={{ "data-testid": "charge-history" }}
          />
        }
        label={intl.formatMessage({ id: "gestion.fansbury.chargeHistory.label" })}
      />
      <RevaText variant="caption" color="text.secondary">
        {intl.formatMessage({ id: "gestion.fansbury.chargeHistory.help" })}
      </RevaText>
    </>
  );

  if (statusLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Box sx={{ mb: 4, maxWidth: "100%", overflowX: "hidden" }}>
      <RevaText variant="h6" sx={{ mb: 1 }}>
        {intl.formatMessage({ id: "gestion.fansbury.title" })}
      </RevaText>
      <RevaText sx={{ mb: 2 }}>{intl.formatMessage({ id: "gestion.fansbury.intro" })}</RevaText>

      <Stack spacing={2} sx={{ maxWidth: "100%", minWidth: 0 }}>
        <TextField
          label={intl.formatMessage({ id: "gestion.fansbury.uuid" })}
          value={organizerId}
          onChange={(event) => setOrganizerId(event.target.value)}
          disabled={uuidLocked || isActive}
          fullWidth
        />

        {status?.can_start && !isActive && (
          <>
            {renderChargeHistoryOption()}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" onClick={handlePreview} disabled={busy || !organizerId}>
                {intl.formatMessage({ id: "gestion.fansbury.preview" })}
              </Button>
              <Button variant="contained" onClick={handleStart} disabled={busy || !organizerId || !preview}>
                {intl.formatMessage({ id: "gestion.fansbury.start" })}
              </Button>
            </Stack>
          </>
        )}

        <Alert
          severity="info"
          icon={false}
          sx={{
            opacity: busy ? 1 : 0,
            height: 68,
            boxSizing: "border-box",
            overflow: "hidden",
            maxWidth: "100%",
            py: "10px",
            alignItems: "center",
            "& .MuiAlert-message": {
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
              py: 0,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, maxWidth: "100%" }}>
            <RevaText sx={{ flex: 1, minWidth: 0 }}>
              {intl.formatMessage({
                id:
                  busyAction === "start"
                    ? "gestion.fansbury.working.start"
                    : busyAction === "remigrate"
                      ? "gestion.fansbury.working.remigrate"
                      : "gestion.fansbury.working.preview",
              })}
            </RevaText>
            <Box
              sx={{
                width: 20,
                height: 20,
                overflow: "hidden",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {busy ? <CircularProgress size={20} /> : null}
            </Box>
          </Stack>
        </Alert>

        {preview && (
          <Alert severity="info">
            <Stack direction="row" spacing={2} alignItems="center">
              {logoUrl ? <Box component="img" src={logoUrl} alt="" sx={{ width: 56, height: 56, objectFit: "contain" }} /> : null}
              <Box>
                <RevaText variant="subtitle1">{displayName}</RevaText>
                <RevaText>
                  {intl.formatMessage(
                    { id: "gestion.fansbury.counts" },
                    {
                      users: counts?.users ?? 0,
                      players: counts?.players ?? 0,
                      teams: counts?.teams ?? 0,
                      tournaments: counts?.tournaments ?? 0,
                    },
                  )}
                </RevaText>
              </Box>
            </Stack>
          </Alert>
        )}

        {runStatus === "queued" && (
          <Alert severity="info">{intl.formatMessage({ id: "gestion.fansbury.queued" })}</Alert>
        )}
        {runStatus === "running" && (
          <Alert severity="info">
            {run?.current_step && run?.steps?.[run.current_step]?.message
              ? intl.formatMessage(
                  { id: "gestion.fansbury.runningPhase" },
                  {
                    step: formatMigrationStep(intl, run.current_step),
                    phase: run.steps[run.current_step].message,
                  },
                )
              : intl.formatMessage({ id: "gestion.fansbury.running" })}
          </Alert>
        )}
        {runStatus === "failed" && (
          <Alert severity="error">
            {intl.formatMessage(
              { id: "gestion.fansbury.failed" },
              {
                step: run?.current_step ? formatMigrationStep(intl, run.current_step) : "—",
                error: run?.error_message || "",
              },
            )}
          </Alert>
        )}
        {runStatus === "done" && (
          <Alert severity="success">{intl.formatMessage({ id: "gestion.fansbury.done" }, { at: run?.finished_at || "" })}</Alert>
        )}

        {stepEntries.length > 0 && (
          <GestionTable
            headers={[
              { key: "step", title: intl.formatMessage({ id: "gestion.fansbury.table.step" }) },
              { key: "status", title: intl.formatMessage({ id: "gestion.fansbury.table.status" }) },
              { key: "count", title: intl.formatMessage({ id: "gestion.fansbury.table.count" }) },
            ]}
            rows={stepEntries.map(([step, meta]) => ({
              id: step,
              step: meta.message
                ? `${formatMigrationStep(intl, step)} — ${meta.message}`
                : formatMigrationStep(intl, step),
              status: (
                <Chip
                  size="small"
                  color={STATUS_CHIP_COLOR[meta.status] || "default"}
                  label={formatMigrationStatus(intl, meta.status)}
                />
              ),
              count: meta.count ?? "—",
            }))}
          />
        )}

        {runStatus === "done" && run?.summary?.counts && (
          <GestionTable
            headers={[
              { key: "step", title: intl.formatMessage({ id: "gestion.fansbury.table.step" }) },
              { key: "count", title: intl.formatMessage({ id: "gestion.fansbury.table.count" }) },
            ]}
            rows={Object.entries(run.summary.counts).map(([step, count]) => ({
              id: step,
              step: formatMigrationStep(intl, step),
              count,
            }))}
          />
        )}

        {status?.can_start && runStatus === "failed" && (
          <Button variant="contained" onClick={handleStart} disabled={busy || !organizerId}>
            {intl.formatMessage({ id: "gestion.fansbury.retry" })}
          </Button>
        )}

        {status?.can_remigrate && (
          <Button color="warning" variant="outlined" onClick={() => {
              setChargeHistory(false);
              setRemigrateOpen(true);
            }} disabled={busy || isActive}>
            {intl.formatMessage({ id: "gestion.fansbury.remigrate" })}
          </Button>
        )}
      </Stack>

      <Dialog open={remigrateOpen} onClose={resetRemigrate}>
        <DialogTitle>{intl.formatMessage({ id: "gestion.fansbury.remigrate.title" })}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {challengeId
              ? intl.formatMessage({ id: "gestion.fansbury.remigrate.step2" })
              : intl.formatMessage({ id: "gestion.fansbury.remigrate.body" }, { name: customerName })}
          </DialogContentText>
          {challengeId && hasFinanceImpact && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {intl.formatMessage({ id: "gestion.fansbury.remigrate.financeImpact" }, financeImpact)}
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={financeAck}
                    disabled={busy}
                    onChange={(event) => setFinanceAck(event.target.checked)}
                    inputProps={{ "data-testid": "finance-ack" }}
                  />
                }
                label={intl.formatMessage({ id: "gestion.fansbury.remigrate.financeAck" })}
              />
            </>
          )}
          {renderChargeHistoryOption()}
          <FormControlLabel
            control={<Checkbox checked={remigrateAck} onChange={(event) => setRemigrateAck(event.target.checked)} />}
            label={intl.formatMessage({ id: "gestion.fansbury.remigrate.ack" })}
          />
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={intl.formatMessage({ id: "gestion.fansbury.remigrate.confirmLabel" })}
            value={remigrateConfirm}
            onChange={(event) => setRemigrateConfirm(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetRemigrate}>{intl.formatMessage({ id: "gestion.fansbury.remigrate.cancel" })}</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy || !canSubmitRemigrate}
            onClick={handleRemigrate}
          >
            {challengeId
              ? intl.formatMessage({ id: "gestion.fansbury.remigrate.confirm" })
              : intl.formatMessage({ id: "gestion.fansbury.remigrate.continue" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionFansburyMigration;
